// chatPageLightTheme.js
// ─────────────────────────────────────────────────────────────────────────────
// "Lavender Haze" — brand-aligned light theme for ChatPage.
// All color tokens used by the light variant of ChatPage live here.
// Dark theme values stay inline in ChatPage.js — this file only feeds the
// `chatIsLight ? ... : ...` ternaries' left-hand (light) branch.
// ─────────────────────────────────────────────────────────────────────────────

// Raw palette (per spec).
export const LAVENDER_HAZE = {
  shell:        "#ece5f8", // full-page background (under everything)
  panel:        "#ddd1f0", // left/right sidebars — denser lavender
  chatArea:     "#f3eefb", // central chat surface
  surface:      "#faf7ff", // cards, bubbles, inputs (near-white tint)
  accent:       "#6d4adb", // primary brand violet
  accentDeep:   "#5a3bc8", // borders / hover on accent
  accentSoft:   "#e7defd", // accent tints, conic-grad rings

  text:         "#1d143f", // primary text
  textMuted:    "#5e5180", // secondary text / labels
  textDim:      "#8a7eaa", // metadata / time / captions

  border:       "rgba(80, 50, 160, 0.14)",
  divider:      "rgba(80, 50, 160, 0.08)",

  headerGradient: "linear-gradient(180deg, #ddd1f0 0%, #cbbbe8 100%)",
  activeChip:   "#ddcefb",
  onAccent:     "#ffffff",
};

const P = LAVENDER_HAZE;

// ─────────────────────────────────────────────────────────────────────────────
// Light-theme tokens consumed by ChatPage.js.
// Names mirror the original `chatIsLight ? "X" : "Y"` sites so the wiring is
// a 1:1 swap of the light-side string with `LH.<name>`.
// ─────────────────────────────────────────────────────────────────────────────
export const LH = {
  // --- CSS variables on .na-chat-page-root (lines ~2630–2656) -----------------
  cssVar: {
    bg:           P.shell,                                  // --na-bg
    bg2:          P.panel,                                  // --na-bg-2
    panel:        "rgba(221, 209, 240, 0.88)",              // --na-panel
    card:         "rgba(250, 247, 255, 0.82)",              // --na-card
    cardStrong:   "rgba(250, 247, 255, 0.96)",              // --na-card-strong
    line:         P.border,                                  // --na-line
    text:         P.text,                                    // --na-text
    soft:         P.textMuted,                               // --na-soft
    muted:        P.textDim,                                 // --na-muted
    yellow:       "#c48400",                                 // --na-yellow
    glassPanel:   "rgba(250, 247, 255, 0.72)",              // --na-glass-panel
    glassMenu:    "rgba(250, 247, 255, 0.96)",              // --na-glass-menu
    glassBorder:  "rgba(109, 74, 219, 0.20)",                // --na-glass-border
    glassShadow:
      "0 16px 38px rgba(80,50,160,.14), " +
      "inset 0 1px 1px rgba(255,255,255,.82), " +
      "inset 0 -1px 1px rgba(80,50,160,.06)",                // --na-glass-shadow
    glassText:    P.text,                                    // --na-glass-text
    chatSurface:
      "radial-gradient(circle at 50% 2%, rgba(109,74,219,.10), transparent 30%), " +
      "linear-gradient(180deg, #f7f2fc 0%, #f3eefb 52%, #efe7f9 100%)", // --na-chat-surface
    rowHover:     "rgba(109, 74, 219, 0.08)",                // --na-row-hover
    rowActive:
      "linear-gradient(90deg, rgba(109,74,219,.20), rgba(231,222,253,.78))", // --na-row-active
    popoverBg:    "rgba(250, 247, 255, 0.96)",              // --na-popover-bg
    inputBg:      "rgba(250, 247, 255, 0.86)",              // --na-input-bg
    bubbleThem:
      "linear-gradient(180deg, rgba(255,255,255,.98), rgba(243,238,251,.96))", // --na-bubble-them
    bubbleSystem:
      "linear-gradient(145deg, rgba(255,255,255,.88), rgba(231,222,253,.78))", // --na-bubble-system
    tableInner:   "rgba(250, 247, 255, 0.96)",              // --na-table-inner
    statusRing:   P.surface,                                 // --na-status-ring
  },

  // --- Root page background (line ~2660) --------------------------------------
  pageBackground:
    "radial-gradient(circle at 18% 0%, rgba(109,74,219,.22), transparent 34%), " +
    "radial-gradient(circle at 86% 12%, rgba(168,140,235,.18), transparent 34%), " +
    "radial-gradient(circle at 54% 100%, rgba(168,85,247,.13), transparent 38%), " +
    "linear-gradient(180deg, #ece5f8 0%, #e3d7f1 56%, #ddd1f0 100%)",

  // --- Brand logo (lines 2888–2889) -------------------------------------------
  logoColor:    P.accent,
  logoShadow:   "0 8px 22px rgba(109,74,219,.18)",

  // --- Inline shadows scattered through redesignCss --------------------------
  shadowSm:     "0 10px 28px rgba(80,50,160,.10)",            // ~2904
  shadowMd:     "0 12px 30px rgba(80,50,160,.10)",            // ~3475, ~3958
  shadowLg:     "0 18px 42px rgba(80,50,160,.14)",            // ~3324
  shadowLg2:    "0 20px 44px rgba(80,50,160,.12)",            // ~3356
  shadowXl:     "0 22px 48px rgba(80,50,160,.16)",            // ~4010
  shadowXl2:    "0 16px 42px rgba(80,50,160,.10)",            // ~4034
  shadow2xl:    "0 30px 70px rgba(80,50,160,.18)",            // ~4198

  // --- Misc colors (lines 3029, 3696) -----------------------------------------
  mutedColor:        "rgba(94,81,128,.72)",
  placeholderColor:  "rgba(94,81,128,.58)",

  // --- Side panels / popover surfaces (lines 3840, 4065–4066, 4159) ----------
  rightbarBg:
    "linear-gradient(180deg, rgba(221,209,240,.72), rgba(221,209,240,.92))",
  popoverInnerBg:
    "linear-gradient(145deg, rgba(255,255,255,.98), rgba(243,238,251,.98))",
  popoverInnerShadow: "inset 0 0 22px rgba(109,74,219,.10)",
  composerBg:        "rgba(243,238,251,.55)",

  // ───────────────────────────────────────────────────────────────────────────
  // Tokens for MessageAnimations / ChatBackground (lines 30–400)
  // ───────────────────────────────────────────────────────────────────────────

  // ChatBackground SVG icons (lines 404–405)
  bgIconFill:    "rgba(109,74,219,0.10)",
  bgIconStroke:  "rgba(109,74,219,0.16)",
  bgPlate:       "#f3eefb",

  // .attachment-menu-* (lines 38–57)
  attachmentText:        P.text,
  attachmentBtnBg:       "rgba(231, 222, 253, 0.55)",
  attachmentBtnBorder:   "rgba(109, 74, 219, 0.22)",
  attachmentBtnHover:    "rgba(221, 209, 240, 0.78)",
  attachmentBtnShadow:   "rgba(80,50,160,0.20)",

  // .profile-section-divider (line 148)
  profileDividerText:    P.text,

  // .liquid-glass (lines 219–223 and hover 229–231)
  liquidGlassBg:         "rgba(231, 222, 253, 0.72)",
  liquidGlassBorder:     "rgba(109, 74, 219, 0.22)",
  liquidGlassShadow:
    "0 8px 32px rgba(80,50,160,0.15), " +
    "inset 0 1px 1px rgba(255,255,255,0.7), " +
    "inset 0 -1px 1px rgba(80,50,160,0.06)",
  liquidGlassText:       P.text,
  liquidGlassHoverBg:    "rgba(221, 209, 240, 0.80)",
  liquidGlassHoverBorder:"rgba(109, 74, 219, 0.40)",
  liquidGlassHoverShadow:
    "0 12px 40px rgba(80,50,160,0.22), inset 0 1px 2px rgba(255,255,255,0.78)",

  // .ai-glass-button (lines 240–246)
  aiGlassBg:             "rgba(231, 222, 253, 0.45)",
  aiGlassBorder:         "rgba(109, 74, 219, 0.28)",
  aiGlassShadow:         "rgba(80,50,160,0.18)",
  aiGlassInset:          "rgba(255,255,255,0.7)",
  aiGlassHoverBg:        "rgba(221, 209, 240, 0.72)",

  // .chat-list-item (lines 307–318)
  chatItemHoverBg:       "rgba(109,74,219,0.07)",
  chatItemHoverEdge:     "rgba(109,74,219,0.30)",
  chatItemActiveBg:      "rgba(109,74,219,0.14)",
  chatItemRipple:        "rgba(109,74,219,0.18)",
};

export default LH;
